import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { useCreateIntegrationEmailMutation } from '../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../common/integrations'
import { type Booking, type BookingClient, type EmailTemplatePurpose, type Location, type SchedulingEventType } from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { type LocationInput } from '../../../store/scheduling/dto'
import { useSchedulingEmailTemplates } from '../shared/useSchedulingEmailTemplates'
import { buildEmailTemplateContext } from './buildEmailTemplateContext'
import { renderEmailHtmlBody, renderEmailSubject } from './renderEmailTemplate'
import { unsubscribeLink } from './clientInviteLinks'

export interface PushBookingToDraftInput {
  booking: Booking
  eventType: SchedulingEventType
  owner?: User
  purpose: EmailTemplatePurpose
  location?: Location | LocationInput | null
  bookingLink?: string
  originalStart?: string
}

export interface DraftRecipient {
  client: BookingClient
  bookingLink: string
}

export const noDraftRecipientsMessage = (savedNotice: string, sharedBookingLink?: string): string =>
  sharedBookingLink ? `${savedNotice}, but no client has an email address.` : `${savedNotice}, but no client has an email and booking link.`

export const resolveDraftRecipients = (clients: BookingClient[], sharedBookingLink?: string): DraftRecipient[] =>
  sharedBookingLink
    ? clients.filter((client) => client.email).map((client) => ({ client, bookingLink: sharedBookingLink }))
    : clients.filter((client) => client.email && client.inviteUrl).map((client) => ({ client, bookingLink: client.inviteUrl ?? '' }))

export type PushToDraftResult =
  | { outcome: 'pushed'; created: number; total: number }
  | { outcome: 'backend-error' }
  | { outcome: 'skipped'; reason: 'not-connected' | 'no-template' | 'no-recipients' }

export interface PushBookingToDraft {
  isEmailConnected: boolean
  isPushing: boolean
  pushToDraft: (input: PushBookingToDraftInput) => Promise<PushToDraftResult>
}

export const usePushBookingToDraft = (): PushBookingToDraft => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: me } = useGetMeQuery({ accountId })
  const { data: accountResponse } = useGetAccountQuery({ accountId })
  const { byPurpose, isLoading, isError } = useSchedulingEmailTemplates()
  const [createIntegrationEmail, { isLoading: isPushing }] = useCreateIntegrationEmailMutation()

  const mailIntegration = me?.integrations?.find((integration) => integration.type === 'mail')

  const pushToDraft = async ({
    booking,
    eventType,
    owner,
    purpose,
    location,
    bookingLink,
    originalStart,
  }: PushBookingToDraftInput): Promise<PushToDraftResult> => {
    if (!mailIntegration) {
      return { outcome: 'skipped', reason: 'not-connected' }
    }
    if (isLoading || isError) {
      return { outcome: 'backend-error' }
    }
    const template = byPurpose(purpose)
    if (!template || !template.subject || !template.body) {
      return { outcome: 'skipped', reason: 'no-template' }
    }
    const recipients = resolveDraftRecipients(booking.clients, bookingLink)
    if (recipients.length === 0) {
      return { outcome: 'skipped', reason: 'no-recipients' }
    }
    const createdExternalIds: string[] = []
    for (const { client, bookingLink: recipientLink } of recipients) {
      const context = buildEmailTemplateContext({
        eventType,
        client,
        owner,
        meetingMode: booking.meetingMode,
        location,
        bookingLink: recipientLink,
        unsubscribeLink: unsubscribeLink(client, purpose),
        account: accountResponse?.data,
        originalStart,
      })
      const result = await createIntegrationEmail({
        accountId,
        connector: mailIntegration.serviceId,
        suppressGlobalErrorToast: true,
        body: {
          subject: renderEmailSubject(template.subject, context),
          body: renderEmailHtmlBody(template.body, context),
          recipients: [{ email: client.email ?? '' }],
          isDraft: true,
        },
      })
      if ('data' in result && result.data) {
        createdExternalIds.push(result.data.externalId)
      }
    }
    if (createdExternalIds.length === 0) {
      return { outcome: 'backend-error' }
    }
    if (mailIntegration.serviceId === IntegrationServiceId.GMAIL) {
      window.open(`https://mail.google.com/mail/u/${mailIntegration.externalId}/#drafts?compose=${createdExternalIds[0]}`, '_blank')
    }
    return { outcome: 'pushed', created: createdExternalIds.length, total: recipients.length }
  }

  return { isEmailConnected: Boolean(mailIntegration), isPushing, pushToDraft }
}
